// packages/auth/src/usePermissions.ts

import { useCallback } from 'react';

import type { SchemaType } from '@alamo/types';

import { useAuth } from './useAuth';

/**
 * Hook for permission checks in components.
 * Returns helper functions instead of raw permission arrays.
 *
 * @example
 * const { hasPermission, hasAnyPermission, hasRole, isMlmEligible } = usePermissions();
 *
 * if (hasPermission('quotation', 'create')) {
 *   // show create button
 * }
 *
 * if (isMlmEligible) {
 *   // show team / commissions / ranking
 * }
 */
export function usePermissions() {
  const { permissions, roleName, schemaType, consultantType } = useAuth();

  const hasPermission = useCallback(
    (resource: string, action: string): boolean => {
      return (
        permissions.includes(`${resource}:${action}`) || permissions.includes(`${resource}:manage`)
      );
    },
    [permissions],
  );

  const hasAnyPermission = useCallback(
    (checks: Array<{ resource: string; action: string }>): boolean => {
      return checks.some(({ resource, action }) => hasPermission(resource, action));
    },
    [hasPermission],
  );

  const hasRole = useCallback(
    (role: string): boolean => {
      return roleName === role;
    },
    [roleName],
  );

  // CLT consultors do not participate in the MLM hierarchy (no parent, no
  // subordinates, no commission split). Guard MLM-only UI with this flag.
  const isMlmEligible = consultantType === 'pf' || consultantType === 'pj';

  return {
    hasPermission,
    hasAnyPermission,
    hasRole,
    userRole: roleName,
    userPortal: schemaType as SchemaType,
    consultantType,
    isMlmEligible,
    permissions,
  };
}
